/* eslint-disable max-len */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AttachFile,
  BottomSection,
  Buttons,
  DescriptionSection,
  Detail,
  Group,
  HeaderText,
  HeaderTitle,
  Input,
  Inputs,
  Leftbuttons,
  OverLayDiv,
  TaskTemplateHeader,
  Text,
  Textarea,
  TextField,
  Top,
  UploadDiv
} from './styles';
import AppLayout from '../appLayout';
import ButtonV2 from '../button/buttonV2';
import Inline from '../comment/inline';
import { useDispatch } from 'react-redux';
import { isEmpty } from 'lodash';
import { handleFileUpload } from '../../helpers/common';
import {
  createNewTaskTemplate,
  dispatchSettingError,
  dispatchSettingSuccess,
  getTaskTemplateList,
  updateTaskTemplateDetails
} from '../../services/settingServices';
import { useHistory, useParams } from 'react-router-dom';
import { ROUTES } from '../../routes/urls';
import SVGIcon from '../../assets/images/svg/SVGIcon';
import { BUTTON_TYPE, FILE_TYPES } from '../../global/constants';
import { captureException } from '../../services/logService';
import { rootStore } from '../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import PriorityDropdownV2 from '../dropdowns/priorityDropdown/priorityDropdownV2';
import AssignBoxV2 from '../dropdowns/assignPopup/assignPopupV2';
import { TaskTemplateModel } from '../../mobx/stores/projectTemplateStore';
import { RawFile } from '../../mobx/interfaces/task';
import { useMobileDevice } from '../../global/useMobile';
import ResponsiveHeader from '../../component/responsiveHeader/header';
import BottomSubNavigation from '../bottomNavigation/subNavigation';
import BottomNavigationItem from '../bottomNavigation/subNavigation/bottomNavigationItem';

const TaskTemplateDetails: React.FC = () => {
  //Refs
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inlineRef = useRef<any>(null);
  //States
  const [fileUploading, setFileUploading] = useState(false);
  const [isAssignDropdownOpen, setIsAssignDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [updatedFile, setUpdatedFile] = useState<RawFile[]>([]);
  const [priorityVersion, setPriorityVersion] = useState(0);

  //Mobx store variables
  const { projectTemplateStore, workspaceStore, projectStore } = rootStore;
  const { currentTaskTemplate } = projectTemplateStore;
  const { currentWorkspace } = workspaceStore;
  const { uploadFilePercent } = projectStore;

  //Other variable
  const dispatch = useDispatch();
  const history = useHistory();
  const params: { id: string } = useParams();
  const isMobile = useMobileDevice();

  const loadData = useCallback(async () => {
    if (isEmpty(currentTaskTemplate?.id) && currentWorkspace?.id) {
      try {
        await dispatch(getTaskTemplateList());
        rootStore.projectTemplateStore.setCurrentTaskTemplate(params?.id);
      } catch (error) {
        captureException(error);
        console.error('Failed to load task template data', error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, params?.id, currentWorkspace?.id]);

  useEffect(() => {
    if (updatedFile && updatedFile.length > 0) {
      currentTaskTemplate?.setFiles(updatedFile);
    }
  }, [currentTaskTemplate, dispatch, updatedFile]);

  useEffect(() => {
    loadData();
    return () => {
      projectTemplateStore.setCurrentTaskTemplate('');
    };
  }, [loadData, projectTemplateStore]);

  useEffect(() => {
    setPriorityVersion((prev) => prev + 1);
  }, [currentTaskTemplate?.priority]);

  const handleFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleInput = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  const onFileUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = event.target;
      if (!files || files.length === 0) {
        dispatchSettingError('No files selected.');
        return;
      }
      try {
        setFileUploading(true);
        const fileArray = Array.from(files); // Convert FileList to Array
        const updatedFiles = await handleFileUpload(currentTaskTemplate?.files || [], fileArray, dispatch, inlineRef); //call common function to upload file in firebase
        const value = [...(currentTaskTemplate?.files || []), ...(updatedFiles || [])];
        setUpdatedFile(value);
      } catch (e) {
        captureException(e);
      } finally {
        setFileUploading(false);
      }
    },
    [currentTaskTemplate?.files, dispatch]
  );
  const handleSubmit = async () => {
    const taskInput: TaskTemplateModel = JSON.parse(JSON.stringify(currentTaskTemplate));
    const updatedFiles = taskInput?.files?.map((file) => {
      return {
        file_type: file.file_type,
        href: file.href,
        size: file.size,
        uploadFileType: FILE_TYPES.UPLOAD,
        name: file.name
      };
    });
    if (!currentTaskTemplate?.templateName || !currentTaskTemplate?.name) {
      dispatchSettingError('Template name and task name are required.');
      return;
    }
    const payload = {
      Title: currentTaskTemplate.templateName,
      TaskTitle: currentTaskTemplate?.name,
      Description: currentTaskTemplate?.description,
      Priority: currentTaskTemplate?.priority,
      Assignee: currentTaskTemplate?.usersIds,
      Attachment: !isEmpty(updatedFiles) ? updatedFiles : []
    };
    try {
      setLoading(true);
      let result;
      if (currentTaskTemplate?.id) {
        result = await dispatch(updateTaskTemplateDetails(currentTaskTemplate?.id, payload));
      } else {
        result = await dispatch(createNewTaskTemplate(payload));
        dispatchSettingSuccess('Task template created successfully!');
      }
      if (result) {
        history.push(ROUTES.TEMPLATES_SETTING);
        projectTemplateStore.setCurrentTaskTemplate('');
      }
    } catch (error) {
      captureException(error);
      console.log('error', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AppLayout>
        <Group>
          {!isMobile ? (
            <TaskTemplateHeader>
              <HeaderTitle>
                <HeaderText>{currentTaskTemplate?.id ? 'Edit Template' : 'New Template'}</HeaderText>
              </HeaderTitle>
              <ButtonV2
                title={currentTaskTemplate?.id ? 'Save Template' : 'Create New'}
                variant={BUTTON_TYPE.PRIMARY}
                isLoading={loading}
                onClick={handleSubmit}
              />
            </TaskTemplateHeader>
          ) : (
            <ResponsiveHeader Title={currentTaskTemplate?.id ? 'Edit Template' : 'New Template'} />
          )}
          <BottomSection>
            <Top>
              <Text>Template Name</Text>
              <Input
                placeholder='Add a descriptive name...'
                type='text'
                value={currentTaskTemplate?.templateName}
                onChange={(e) => currentTaskTemplate?.setTemplateName(e.target.value)}
              />
            </Top>
            <Inputs>
              <TextField>
                <Textarea
                  placeholder='Task Title'
                  rows={1}
                  ref={textareaRef}
                  onInput={handleInput}
                  value={currentTaskTemplate?.name}
                  onChange={(e) => currentTaskTemplate?.setTaskName(e.target.value)}
                />
                <DescriptionSection>
                  <Inline
                    ref={inlineRef}
                    isTaskModel={true}
                    uploadedFiles={[...(currentTaskTemplate?.files || [])]}
                    setUploadedFiles={setUpdatedFile}
                    fieldValue={currentTaskTemplate?.description || ''}
                    setValueContent={(value: string) => currentTaskTemplate?.setDescription(value)}
                    placeholder={'Add Description....'}
                    isApplyMaxHeight={240}
                    isApplyMinHeight={90}
                    isCreateTask
                    propsLoading={loading}
                    setPropsLoading={(value: boolean) => setLoading(value)}
                    isScrollbarDesign
                  />
                </DescriptionSection>
              </TextField>
              {fileUploading && <UploadDiv>Uploading ({Math.floor(uploadFilePercent)}%)</UploadDiv>}
              <Buttons>
                <Leftbuttons>
                  <Detail>
                    <AssignBoxV2
                      setIsDropdownOpen={setIsAssignDropdownOpen}
                      isHumanIcon={true}
                      projectUsers={currentWorkspace?.users || []}
                      isTaskTemplate={true}
                    />
                  </Detail>
                  <Detail>
                    <PriorityDropdownV2
                      key={`priority-${priorityVersion}`}
                      isCreateTask={true}
                      isTaskTemplate={true}
                      currentTaskTemplate={currentTaskTemplate || undefined}
                    />
                  </Detail>
                </Leftbuttons>
                <input type='file' ref={fileInputRef} style={{ display: 'none' }} onChange={onFileUpload} multiple />
                <AttachFile onClick={handleFileInput}>
                  <SVGIcon
                    name='attachment-icon'
                    width='16'
                    height='16'
                    viewBox='0 0 18 18'
                    fill='var(--text-secondary)'
                  />
                </AttachFile>
              </Buttons>
            </Inputs>
          </BottomSection>
        </Group>
        {isAssignDropdownOpen && (
          <OverLayDiv
            className='dropdown-overlay'
            onClick={() => {
              setIsAssignDropdownOpen(false);
            }}
          />
        )}
      </AppLayout>
      <BottomSubNavigation
        rightElement={<BottomNavigationItem iconName='correct-sign-icon' onClick={handleSubmit} />}
      />
    </>
  );
};

export default observer(TaskTemplateDetails);
